/**
 * Centralized Country Data
 * ISO 3166-1 alpha-2 country codes with i18n support
 *
 * Priority ordering: Switzerland (CH), Germany (DE), Austria (AT) always first
 * Remaining countries sorted alphabetically by English name
 */

export interface Country {
  code: string; // ISO 3166-1 alpha-2 (e.g., "CH", "DE", "AT")
  translationKey: string; // i18n translation key (e.g., "countries.CH")
  priority?: number; // Optional: 1 = Switzerland, 2 = Germany, 3 = Austria
}

/**
 * Priority countries (always displayed first)
 */
export const PRIORITY_COUNTRIES: Country[] = [
  { code: 'CH', translationKey: 'countries.CH', priority: 1 }, // Switzerland
  { code: 'DE', translationKey: 'countries.DE', priority: 2 }, // Germany
  { code: 'AT', translationKey: 'countries.AT', priority: 3 }, // Austria
];

/**
 * All other countries (alphabetically sorted by English name)
 * Total: 193 countries (196 total including priority countries)
 */
export const OTHER_COUNTRIES: Country[] = [
  { code: 'AF', translationKey: 'countries.AF' }, // Afghanistan
  { code: 'AL', translationKey: 'countries.AL' }, // Albania
  { code: 'DZ', translationKey: 'countries.DZ' }, // Algeria
  { code: 'AD', translationKey: 'countries.AD' }, // Andorra
  { code: 'AO', translationKey: 'countries.AO' }, // Angola
  { code: 'AG', translationKey: 'countries.AG' }, // Antigua and Barbuda
  { code: 'AR', translationKey: 'countries.AR' }, // Argentina
  { code: 'AM', translationKey: 'countries.AM' }, // Armenia
  { code: 'AU', translationKey: 'countries.AU' }, // Australia
  { code: 'AZ', translationKey: 'countries.AZ' }, // Azerbaijan
  { code: 'BS', translationKey: 'countries.BS' }, // Bahamas
  { code: 'BH', translationKey: 'countries.BH' }, // Bahrain
  { code: 'BD', translationKey: 'countries.BD' }, // Bangladesh
  { code: 'BB', translationKey: 'countries.BB' }, // Barbados
  { code: 'BY', translationKey: 'countries.BY' }, // Belarus
  { code: 'BE', translationKey: 'countries.BE' }, // Belgium
  { code: 'BZ', translationKey: 'countries.BZ' }, // Belize
  { code: 'BJ', translationKey: 'countries.BJ' }, // Benin
  { code: 'BT', translationKey: 'countries.BT' }, // Bhutan
  { code: 'BO', translationKey: 'countries.BO' }, // Bolivia
  { code: 'BA', translationKey: 'countries.BA' }, // Bosnia and Herzegovina
  { code: 'BW', translationKey: 'countries.BW' }, // Botswana
  { code: 'BR', translationKey: 'countries.BR' }, // Brazil
  { code: 'BN', translationKey: 'countries.BN' }, // Brunei
  { code: 'BG', translationKey: 'countries.BG' }, // Bulgaria
  { code: 'BF', translationKey: 'countries.BF' }, // Burkina Faso
  { code: 'BI', translationKey: 'countries.BI' }, // Burundi
  { code: 'CV', translationKey: 'countries.CV' }, // Cabo Verde
  { code: 'KH', translationKey: 'countries.KH' }, // Cambodia
  { code: 'CM', translationKey: 'countries.CM' }, // Cameroon
  { code: 'CA', translationKey: 'countries.CA' }, // Canada
  { code: 'CF', translationKey: 'countries.CF' }, // Central African Republic
  { code: 'TD', translationKey: 'countries.TD' }, // Chad
  { code: 'CL', translationKey: 'countries.CL' }, // Chile
  { code: 'CN', translationKey: 'countries.CN' }, // China
  { code: 'CO', translationKey: 'countries.CO' }, // Colombia
  { code: 'KM', translationKey: 'countries.KM' }, // Comoros
  { code: 'CG', translationKey: 'countries.CG' }, // Congo
  { code: 'CD', translationKey: 'countries.CD' }, // Congo (Democratic Republic)
  { code: 'CR', translationKey: 'countries.CR' }, // Costa Rica
  { code: 'HR', translationKey: 'countries.HR' }, // Croatia
  { code: 'CU', translationKey: 'countries.CU' }, // Cuba
  { code: 'CY', translationKey: 'countries.CY' }, // Cyprus
  { code: 'CZ', translationKey: 'countries.CZ' }, // Czechia
  { code: 'CI', translationKey: 'countries.CI' }, // Côte d'Ivoire
  { code: 'DK', translationKey: 'countries.DK' }, // Denmark
  { code: 'DJ', translationKey: 'countries.DJ' }, // Djibouti
  { code: 'DM', translationKey: 'countries.DM' }, // Dominica
  { code: 'DO', translationKey: 'countries.DO' }, // Dominican Republic
  { code: 'EC', translationKey: 'countries.EC' }, // Ecuador
  { code: 'EG', translationKey: 'countries.EG' }, // Egypt
  { code: 'SV', translationKey: 'countries.SV' }, // El Salvador
  { code: 'GQ', translationKey: 'countries.GQ' }, // Equatorial Guinea
  { code: 'ER', translationKey: 'countries.ER' }, // Eritrea
  { code: 'EE', translationKey: 'countries.EE' }, // Estonia
  { code: 'SZ', translationKey: 'countries.SZ' }, // Eswatini
  { code: 'ET', translationKey: 'countries.ET' }, // Ethiopia
  { code: 'FJ', translationKey: 'countries.FJ' }, // Fiji
  { code: 'FI', translationKey: 'countries.FI' }, // Finland
  { code: 'FR', translationKey: 'countries.FR' }, // France
  { code: 'GA', translationKey: 'countries.GA' }, // Gabon
  { code: 'GM', translationKey: 'countries.GM' }, // Gambia
  { code: 'GE', translationKey: 'countries.GE' }, // Georgia
  { code: 'GH', translationKey: 'countries.GH' }, // Ghana
  { code: 'GR', translationKey: 'countries.GR' }, // Greece
  { code: 'GD', translationKey: 'countries.GD' }, // Grenada
  { code: 'GT', translationKey: 'countries.GT' }, // Guatemala
  { code: 'GN', translationKey: 'countries.GN' }, // Guinea
  { code: 'GW', translationKey: 'countries.GW' }, // Guinea-Bissau
  { code: 'GY', translationKey: 'countries.GY' }, // Guyana
  { code: 'HT', translationKey: 'countries.HT' }, // Haiti
  { code: 'HN', translationKey: 'countries.HN' }, // Honduras
  { code: 'HU', translationKey: 'countries.HU' }, // Hungary
  { code: 'IS', translationKey: 'countries.IS' }, // Iceland
  { code: 'IN', translationKey: 'countries.IN' }, // India
  { code: 'ID', translationKey: 'countries.ID' }, // Indonesia
  { code: 'IR', translationKey: 'countries.IR' }, // Iran
  { code: 'IQ', translationKey: 'countries.IQ' }, // Iraq
  { code: 'IE', translationKey: 'countries.IE' }, // Ireland
  { code: 'IL', translationKey: 'countries.IL' }, // Israel
  { code: 'IT', translationKey: 'countries.IT' }, // Italy
  { code: 'JM', translationKey: 'countries.JM' }, // Jamaica
  { code: 'JP', translationKey: 'countries.JP' }, // Japan
  { code: 'JO', translationKey: 'countries.JO' }, // Jordan
  { code: 'KZ', translationKey: 'countries.KZ' }, // Kazakhstan
  { code: 'KE', translationKey: 'countries.KE' }, // Kenya
  { code: 'KI', translationKey: 'countries.KI' }, // Kiribati
  { code: 'KP', translationKey: 'countries.KP' }, // Korea (North)
  { code: 'KR', translationKey: 'countries.KR' }, // Korea (South)
  { code: 'KW', translationKey: 'countries.KW' }, // Kuwait
  { code: 'KG', translationKey: 'countries.KG' }, // Kyrgyzstan
  { code: 'LA', translationKey: 'countries.LA' }, // Laos
  { code: 'LV', translationKey: 'countries.LV' }, // Latvia
  { code: 'LB', translationKey: 'countries.LB' }, // Lebanon
  { code: 'LS', translationKey: 'countries.LS' }, // Lesotho
  { code: 'LR', translationKey: 'countries.LR' }, // Liberia
  { code: 'LY', translationKey: 'countries.LY' }, // Libya
  { code: 'LI', translationKey: 'countries.LI' }, // Liechtenstein
  { code: 'LT', translationKey: 'countries.LT' }, // Lithuania
  { code: 'LU', translationKey: 'countries.LU' }, // Luxembourg
  { code: 'MG', translationKey: 'countries.MG' }, // Madagascar
  { code: 'MW', translationKey: 'countries.MW' }, // Malawi
  { code: 'MY', translationKey: 'countries.MY' }, // Malaysia
  { code: 'MV', translationKey: 'countries.MV' }, // Maldives
  { code: 'ML', translationKey: 'countries.ML' }, // Mali
  { code: 'MT', translationKey: 'countries.MT' }, // Malta
  { code: 'MH', translationKey: 'countries.MH' }, // Marshall Islands
  { code: 'MR', translationKey: 'countries.MR' }, // Mauritania
  { code: 'MU', translationKey: 'countries.MU' }, // Mauritius
  { code: 'MX', translationKey: 'countries.MX' }, // Mexico
  { code: 'FM', translationKey: 'countries.FM' }, // Micronesia
  { code: 'MD', translationKey: 'countries.MD' }, // Moldova
  { code: 'MC', translationKey: 'countries.MC' }, // Monaco
  { code: 'MN', translationKey: 'countries.MN' }, // Mongolia
  { code: 'ME', translationKey: 'countries.ME' }, // Montenegro
  { code: 'MA', translationKey: 'countries.MA' }, // Morocco
  { code: 'MZ', translationKey: 'countries.MZ' }, // Mozambique
  { code: 'MM', translationKey: 'countries.MM' }, // Myanmar
  { code: 'NA', translationKey: 'countries.NA' }, // Namibia
  { code: 'NR', translationKey: 'countries.NR' }, // Nauru
  { code: 'NP', translationKey: 'countries.NP' }, // Nepal
  { code: 'NL', translationKey: 'countries.NL' }, // Netherlands
  { code: 'NZ', translationKey: 'countries.NZ' }, // New Zealand
  { code: 'NI', translationKey: 'countries.NI' }, // Nicaragua
  { code: 'NE', translationKey: 'countries.NE' }, // Niger
  { code: 'NG', translationKey: 'countries.NG' }, // Nigeria
  { code: 'MK', translationKey: 'countries.MK' }, // North Macedonia
  { code: 'NO', translationKey: 'countries.NO' }, // Norway
  { code: 'OM', translationKey: 'countries.OM' }, // Oman
  { code: 'PK', translationKey: 'countries.PK' }, // Pakistan
  { code: 'PW', translationKey: 'countries.PW' }, // Palau
  { code: 'PS', translationKey: 'countries.PS' }, // Palestine
  { code: 'PA', translationKey: 'countries.PA' }, // Panama
  { code: 'PG', translationKey: 'countries.PG' }, // Papua New Guinea
  { code: 'PY', translationKey: 'countries.PY' }, // Paraguay
  { code: 'PE', translationKey: 'countries.PE' }, // Peru
  { code: 'PH', translationKey: 'countries.PH' }, // Philippines
  { code: 'PL', translationKey: 'countries.PL' }, // Poland
  { code: 'PT', translationKey: 'countries.PT' }, // Portugal
  { code: 'QA', translationKey: 'countries.QA' }, // Qatar
  { code: 'RO', translationKey: 'countries.RO' }, // Romania
  { code: 'RU', translationKey: 'countries.RU' }, // Russia
  { code: 'RW', translationKey: 'countries.RW' }, // Rwanda
  { code: 'KN', translationKey: 'countries.KN' }, // Saint Kitts and Nevis
  { code: 'LC', translationKey: 'countries.LC' }, // Saint Lucia
  { code: 'VC', translationKey: 'countries.VC' }, // Saint Vincent and the Grenadines
  { code: 'WS', translationKey: 'countries.WS' }, // Samoa
  { code: 'SM', translationKey: 'countries.SM' }, // San Marino
  { code: 'ST', translationKey: 'countries.ST' }, // Sao Tome and Principe
  { code: 'SA', translationKey: 'countries.SA' }, // Saudi Arabia
  { code: 'SN', translationKey: 'countries.SN' }, // Senegal
  { code: 'RS', translationKey: 'countries.RS' }, // Serbia
  { code: 'SC', translationKey: 'countries.SC' }, // Seychelles
  { code: 'SL', translationKey: 'countries.SL' }, // Sierra Leone
  { code: 'SG', translationKey: 'countries.SG' }, // Singapore
  { code: 'SK', translationKey: 'countries.SK' }, // Slovakia
  { code: 'SI', translationKey: 'countries.SI' }, // Slovenia
  { code: 'SB', translationKey: 'countries.SB' }, // Solomon Islands
  { code: 'SO', translationKey: 'countries.SO' }, // Somalia
  { code: 'ZA', translationKey: 'countries.ZA' }, // South Africa
  { code: 'SS', translationKey: 'countries.SS' }, // South Sudan
  { code: 'ES', translationKey: 'countries.ES' }, // Spain
  { code: 'LK', translationKey: 'countries.LK' }, // Sri Lanka
  { code: 'SD', translationKey: 'countries.SD' }, // Sudan
  { code: 'SR', translationKey: 'countries.SR' }, // Suriname
  { code: 'SE', translationKey: 'countries.SE' }, // Sweden
  { code: 'SY', translationKey: 'countries.SY' }, // Syria
  { code: 'TJ', translationKey: 'countries.TJ' }, // Tajikistan
  { code: 'TZ', translationKey: 'countries.TZ' }, // Tanzania
  { code: 'TH', translationKey: 'countries.TH' }, // Thailand
  { code: 'TL', translationKey: 'countries.TL' }, // Timor-Leste
  { code: 'TG', translationKey: 'countries.TG' }, // Togo
  { code: 'TO', translationKey: 'countries.TO' }, // Tonga
  { code: 'TT', translationKey: 'countries.TT' }, // Trinidad and Tobago
  { code: 'TN', translationKey: 'countries.TN' }, // Tunisia
  { code: 'TR', translationKey: 'countries.TR' }, // Turkey
  { code: 'TM', translationKey: 'countries.TM' }, // Turkmenistan
  { code: 'TV', translationKey: 'countries.TV' }, // Tuvalu
  { code: 'UG', translationKey: 'countries.UG' }, // Uganda
  { code: 'UA', translationKey: 'countries.UA' }, // Ukraine
  { code: 'AE', translationKey: 'countries.AE' }, // United Arab Emirates
  { code: 'GB', translationKey: 'countries.GB' }, // United Kingdom
  { code: 'US', translationKey: 'countries.US' }, // United States
  { code: 'UY', translationKey: 'countries.UY' }, // Uruguay
  { code: 'UZ', translationKey: 'countries.UZ' }, // Uzbekistan
  { code: 'VU', translationKey: 'countries.VU' }, // Vanuatu
  { code: 'VA', translationKey: 'countries.VA' }, // Vatican City
  { code: 'VE', translationKey: 'countries.VE' }, // Venezuela
  { code: 'VN', translationKey: 'countries.VN' }, // Vietnam
  { code: 'YE', translationKey: 'countries.YE' }, // Yemen
  { code: 'ZM', translationKey: 'countries.ZM' }, // Zambia
  { code: 'ZW', translationKey: 'countries.ZW' }, // Zimbabwe
];

/**
 * Complete list of all countries with priority ordering
 * Switzerland (CH), Germany (DE), Austria (AT) always appear first
 * Remaining countries sorted alphabetically
 */
export const ALL_COUNTRIES: Country[] = [
  ...PRIORITY_COUNTRIES,
  ...OTHER_COUNTRIES,
];

/**
 * Get country by ISO code
 */
export const getCountryByCode = (code: string): Country | undefined => {
  return ALL_COUNTRIES.find(country => country.code === code);
};

/**
 * Check if a country is a priority country
 */
export const isPriorityCountry = (code: string): boolean => {
  return PRIORITY_COUNTRIES.some(country => country.code === code);
};
